// Meta (Facebook / Instagram) Pixel for paid-social measurement.
//
// Off unless ALL of these hold:
//   1. REACT_APP_META_PIXEL_ID is set at build time (numeric id; never a token).
//   2. The page is on a production host, or a host listed in REACT_APP_META_PIXEL_HOSTS
//      (comma separated, e.g. a staging host used with a separate test pixel).
//   3. The visitor chose "Accept all" (marketing consent) in the cookie banner.
//   4. The browser is not excluded from analytics (?zedi_exclude_analytics=1) and is not the
//      prerender robot.
// Automatic event detection (autoConfig) and automatic history PageViews (disablePushState) are
// off. Automatic Advanced Matching is an Events Manager setting: keep it OFF for this pixel.
// Conversions are only reported after the Backend confirms acceptance, never on click.
import { hasMarketingConsent } from "./consent";
import { canUseProductionGa, getRouteAnalyticsMetadata, isAnalyticsExcluded } from "./analytics";

const PIXEL_ID_PATTERN = /^\d{6,20}$/;
const FBCLID_PATTERN = /^[A-Za-z0-9_-]{8,500}$/;
const COOKIE_VALUE_PATTERN = /^fb\.\d\.\d{10,16}\.[A-Za-z0-9_.-]{1,500}$/;
const SKIPPED_PAGE_CATEGORIES = new Set(["app", "auth", "billing", "internal"]);

let capturedFbclid = null;
let capturedAt = null;
let initializedId = null;
let lastPageViewPath = null;

const env = () => process.env || {};

export const getMetaPixelId = (vars = env()) => {
  const id = String(vars.REACT_APP_META_PIXEL_ID || "").trim();
  return PIXEL_ID_PATTERN.test(id) ? id : null;
};

const extraHosts = (vars = env()) =>
  String(vars.REACT_APP_META_PIXEL_HOSTS || "")
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

export const isMetaPixelHostAllowed = (hostname = "", vars = env()) => {
  const host = String(hostname || "").toLowerCase();
  return canUseProductionGa(host) || extraHosts(vars).includes(host);
};

const currentHost = () => (typeof window !== "undefined" && window.location ? window.location.hostname : "");

/** True when a pixel exists for this build and host (consent is checked separately). */
export const isMetaPixelConfigured = ({ hostname = currentHost(), vars = env() } = {}) =>
  Boolean(getMetaPixelId(vars)) && isMetaPixelHostAllowed(hostname, vars);

const isPrerender = () =>
  typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "");

export const canTrackMeta = () =>
  typeof window !== "undefined" &&
  isMetaPixelConfigured() &&
  hasMarketingConsent() &&
  !isAnalyticsExcluded() &&
  !isPrerender();

/**
 * Remember the ad click id from the landing URL in memory only (no device storage), so a lead
 * submitted later in the same visit can be tied back to the ad. Called on import and on submit.
 */
export const captureFbclid = (search = typeof window !== "undefined" ? window.location.search : "") => {
  try {
    const value = new URLSearchParams(search || "").get("fbclid");
    if (value && FBCLID_PATTERN.test(value) && value !== capturedFbclid) {
      capturedFbclid = value;
      capturedAt = Date.now();
    }
  } catch (_) {}
  return capturedFbclid;
};
captureFbclid();

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = String(document.cookie || "")
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return null;
  const value = decodeURIComponent(match.slice(name.length + 1));
  return COOKIE_VALUE_PATTERN.test(value) ? value : null;
};

/**
 * Click context attached to a lead. The fbclid is first-party attribution for our own records.
 * The Meta browser identifiers (fbc / fbp) are only included with marketing consent.
 */
export const getMetaClickContext = () => {
  captureFbclid();
  const out = {};
  if (capturedFbclid) out.fbclid = capturedFbclid;
  if (hasMarketingConsent()) {
    const fbc = readCookie("_fbc") || (capturedFbclid ? `fb.1.${capturedAt}.${capturedFbclid}` : null);
    const fbp = readCookie("_fbp");
    if (fbc) out.fbc = fbc;
    if (fbp) out.fbp = fbp;
  }
  return out;
};

export const newMetaEventId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch (_) {}
  return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const ensurePixel = () => {
  if (!canTrackMeta()) return false;
  const id = getMetaPixelId();
  if (!window.fbq) {
    const fbq = function fbqShim() {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    // Stop fbevents.js sending its own PageView on history changes; MetaPixelTracker owns
    // PageView so signed-in, billing and internal routes are never reported.
    fbq.disablePushState = true;
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    const script = document.createElement("script");
    script.async = true;
    script.id = "zedi-meta-pixel";
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }
  window.fbq("consent", "grant");
  if (initializedId !== id) {
    window.fbq("set", "autoConfig", false, id);
    window.fbq("init", id);
    initializedId = id;
  }
  return true;
};

/** One PageView per public route change. Signed-in, billing and internal routes are skipped. */
export const trackMetaPageView = (pathname = "/") => {
  if (!canTrackMeta()) return false;
  const meta = getRouteAnalyticsMetadata(pathname);
  if (meta.requiresAuth || SKIPPED_PAGE_CATEGORIES.has(meta.page_category)) return false;
  if (lastPageViewPath === pathname) return false;
  if (!ensurePixel()) return false;
  lastPageViewPath = pathname;
  window.fbq("track", "PageView");
  return true;
};

/** Lead, reported only after the Backend accepted the enquiry. eventId deduplicates with CAPI. */
export const trackMetaLead = ({ eventId, leadType, businessCategory } = {}) => {
  if (!eventId || !ensurePixel()) return false;
  window.fbq(
    "track",
    "Lead",
    { content_name: "for_shops", content_category: leadType || "enquiry", business_category: businessCategory || undefined },
    { eventID: eventId }
  );
  return true;
};

export const trackMetaCustom = (name, params = {}) => {
  if (!/^[A-Za-z][A-Za-z0-9_]{1,40}$/.test(String(name || "")) || !ensurePixel()) return false;
  window.fbq("trackCustom", name, params);
  return true;
};

const expireCookie = (name) => {
  try {
    const host = currentHost();
    const domains = ["", host, host.replace(/^www\./, ""), `.${host.replace(/^www\./, "")}`];
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    });
  } catch (_) {}
};

/** Called when marketing consent is withdrawn: stop the pixel and clear its first-party cookies. */
export const revokeMetaPixel = () => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") window.fbq("consent", "revoke");
  lastPageViewPath = null;
  expireCookie("_fbp");
  expireCookie("_fbc");
};

// Test hook.
export const __resetMetaPixelForTests = () => {
  capturedFbclid = null;
  capturedAt = null;
  initializedId = null;
  lastPageViewPath = null;
};
